import { Stack } from '@mui/material';
import { Flex } from 'components/shared/flex';
import { AutocompleteSelectField, NumberField, SelectField, StringField } from 'components/shared/form/input';
import Modal, { ModalState } from 'components/shared/modal';
import ThemedButton from 'components/shared/themed_button';
import { ExColumn } from 'entities/ex_column';
import { InstrumentSearchForm } from 'entities/instrument';
import { InstrumentCategory } from 'entities/instrument_category';
import { User } from 'entities/user';
import { Form } from 'rac';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

export const parseExColumnValues = (query: URLSearchParams): { [key: number]: string } => {
  const result: { [key: number]: string } = {};

  // URLのクエリパラメータをループして、ex_column_プレフィックスのものを抽出
  Array.from(query.entries()).forEach(([key, value]) => {
    if (key.startsWith('ex_column_')) {
      const columnId = parseInt(key.replace('ex_column_', ''));
      if (!isNaN(columnId) && value) {
        result[columnId] = value;
      }
    }
  });

  return result;
};

type DetailSearchModalProps = {
  modalState: ModalState;
  onSubmit: () => void;
  searchForm: Form<InstrumentSearchForm>;
  users?: User[];
  instrumentCategories?: InstrumentCategory[];
  exColumns?: ExColumn[];
};

export const DetailSearchModal = ({
  modalState,
  onSubmit,
  searchForm,
  users,
  instrumentCategories,
  exColumns,
}: DetailSearchModalProps) => {
  const { classes } = useStyles();
  const instrumentInfoAttributeExColumns =
    exColumns?.filter(
      (column) =>
        column.extensionClass === 'InstrumentInfoAttribute' && (!column.clientableId || !column.clientableType)
    ) ?? [];

  // ExColumnのinputTypeに応じた入力フィールドを生成する関数
  const renderExColumnField = (column: ExColumn) => {
    if (!column.id || !column.inputType) return null;

    switch (column.inputType) {
      case 'integer':
        return (
          <NumberField
            label={column.name}
            attr={['exColumnValues', column.id]}
            form={searchForm}
            placeholder={`${column.name}で検索`}
          />
        );
      case 'dropdown':
        return (
          <SelectField
            labelId={`ex-column-${column.id}-select`}
            label={column.name}
            attr={['exColumnValues', column.id]}
            form={searchForm}
            data={(column.options || []).map((option) => ({ value: option, label: option }))}
            includeBlank
            blankText="指定なし"
          />
        );
      case 'price':
        return (
          <NumberField
            label={column.name}
            attr={['exColumnValues', column.id]}
            form={searchForm}
            placeholder={`${column.name}で検索`}
          />
        );
      case 'string':
      case 'text':
      default:
        return (
          <StringField
            label={column.name}
            attr={['exColumnValues', column.id]}
            form={searchForm}
            placeholder={`${column.name}で検索`}
          />
        );
    }
  };

  return (
    <Modal
      modalId="detail-search-modal"
      title="詳細検索"
      modalState={modalState}
      size="md"
      footer={
        <>
          <ThemedButton onClick={() => modalState.setOpen(false)} color="secondary">
            キャンセル
          </ThemedButton>
          <ThemedButton onClick={onSubmit} color="success">
            検索
          </ThemedButton>
        </>
      }
    >
      <Stack className={classes.formMargin}>
        <label className={classes.formMargin}>機器情報</label>
        <Flex className={classes.formMargin}>
          <StringField label="メーカー" attr={['q', 'instrumentBasicInfoMakerCont']} form={searchForm} />
          <StringField label="型番" attr={['q', 'instrumentBasicInfoModelNumberCont']} form={searchForm} />
        </Flex>
        <Flex className={classes.formMargin}>
          <StringField label="名称" attr={['q', 'instrumentBasicInfoNameCont']} form={searchForm} />
        </Flex>
        {users && users.length > 0 && (
          <Flex className={classes.formMargin}>
            <AutocompleteSelectField
              form={searchForm}
              labelId="manager-label"
              attr={['q', 'instrumentManagerIdEq']}
              label="担当者"
              data={users.map((u) => ({ label: `${u.familyName} ${u.givenName}`, value: u.id })) || []}
            />
          </Flex>
        )}
        {instrumentCategories && instrumentCategories.length > 0 && (
          <Flex className={classes.formMargin}>
            <AutocompleteSelectField
              form={searchForm}
              attr={['q', 'instrumentBasicInfoInstrumentCategoryIdEq']}
              data={instrumentCategories.map((instrumentCategory) => {
                return { value: instrumentCategory.id!, label: instrumentCategory.name! };
              })}
              labelId="select-instrument-categories"
              label="機器カテゴリ"
            />
          </Flex>
        )}
        <Flex className={classes.formMargin}>
          <StringField label="シリアル番号" attr={['q', 'instrumentBasicInfoSerialNumberCont']} form={searchForm} />
          <StringField label="管理番号" attr={['q', 'instrumentBasicInfoManageNumberCont']} form={searchForm} />
        </Flex>
        <Flex className={classes.formMargin}>
          <StringField label="機器の説明等" attr={['q', 'instrumentBasicInfoNoteCont']} form={searchForm} />
        </Flex>
      </Stack>

      {instrumentInfoAttributeExColumns.length > 0 && (
        <Stack className={classes.formMargin}>
          <label className={classes.formMargin}>製品管理情報</label>
          {instrumentInfoAttributeExColumns.map((column) => (
            <Flex className={classes.formMargin} key={`ex-column-${column.id}`}>
              {renderExColumnField(column)}
            </Flex>
          ))}
        </Stack>
      )}
    </Modal>
  );
};

const useStyles = makeStyles()({
  formMargin: {
    marginBottom: 10,
  },
});
